import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";

const lesVoilesDeSaintTropez: ChauffeurPage = {
  slug: "les-voiles-de-saint-tropez",
  title: "Chauffeur privé Les Voiles de Saint-Tropez – VTC",
  subtitle:
    "Chauffeur privé pendant Les Voiles de Saint-Tropez : accès au vieux port malgré les embouteillages de la presqu'île, transferts aéroport et soirées.",
  keywords: [
    "chauffeur les voiles de saint-tropez",
    "vtc voiles de saint-tropez",
    "chauffeur privé saint-tropez régates",
  ],
  hero: {
    title: "Chauffeur privé pendant Les Voiles de Saint-Tropez",
    subtitle:
      "Régates de yachts de tradition dans le golfe : un chauffeur dédié quand la presqu'île sature et que le vieux port devient inaccessible en voiture.",
    keywords: [
      "chauffeur Les Voiles de Saint-Tropez",
      "VTC vieux port Saint-Tropez",
      "transfert aéroport Nice Saint-Tropez",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé pendant Les Voiles de Saint-Tropez",
    badges: ["Transfert aéroport", "Accès vieux port", "Soirées & retours"],
  },
  benefits: {
    heading: "Pourquoi un chauffeur pendant Les Voiles",
    items: [
      {
        title: "Presqu'île engorgée maîtrisée",
        description:
          "Fin septembre, la route unique de Saint-Tropez sature. Le chauffeur connaît les horaires et les accès pour vous rapprocher du vieux port.",
      },
      {
        title: "Transfert aéroport de Nice",
        description:
          "Nice Côte d'Azur est à une centaine de kilomètres. Suivi de vol, accueil pancarte et trajet direct vers votre hôtel de la presqu'île.",
      },
      {
        title: "Stationnement impossible géré",
        description:
          "Se garer près du port pendant les régates relève de l'exploit : le chauffeur dépose au plus près et repart, vous n'y pensez plus.",
      },
      {
        title: "5,0/5 sur 38 avis Google",
        description:
          "Ponctualité, discrétion et véhicules soignés : la régularité qui vaut à KS Rent Services une note de 5,0/5 sur 38 avis Google.",
      },
    ],
  },
  services: {
    heading: "Services chauffeur pendant Les Voiles de Saint-Tropez",
    services: [
      {
        title: "Transfert aéroport ↔ Saint-Tropez",
        text: "Nice ou Toulon-Hyères vers Saint-Tropez, Ramatuelle et Gassin, avec suivi de vol.",
      },
      {
        title: "Accès au vieux port",
        text: "Dépose au plus près des quais et du village des régates selon la circulation et les fermetures.",
      },
      {
        title: "Mise à disposition journée",
        text: "Chauffeur dédié pour enchaîner port, déjeuners et remises des prix.",
      },
      {
        title: "Soirées & retours de nuit",
        text: "Dîners à Saint-Tropez, Ramatuelle ou Gassin, retour tardif avec un chauffeur qui patiente.",
      },
      {
        title: "Navettes invités & équipages",
        text: "Coordination multi-véhicules pour invités, armateurs et équipages.",
      },
    ],
  },
  locations: {
    heading: "Points clés pendant Les Voiles de Saint-Tropez",
    items: [
      {
        name: "Vieux port de Saint-Tropez",
        description: "Quais, village des Voiles et départs des régates.",
      },
      {
        name: "La Ponche & centre historique",
        description: "Ruelles, places et terrasses du cœur de Saint-Tropez.",
      },
      {
        name: "Ramatuelle & Gassin",
        description: "Villas, domaines et hôtels de la presqu'île.",
      },
      {
        name: "Aéroport de Nice Côte d'Azur",
        description: "À une centaine de kilomètres, terminaux 1 et 2.",
      },
      {
        name: "Sainte-Maxime & golfe",
        description:
          "Rive nord du golfe, alternative d'hébergement et de navette maritime.",
      },
    ],
  },
  generic: {
    heading: "Les Voiles de Saint-Tropez vues d'un chauffeur",
    subheading:
      "Chaque année à la fin de septembre, le golfe se couvre de voiliers de tradition et le vieux port devient le cœur du monde de la voile.",
    paragraphs: [
      "Chaque automne, fin septembre et début octobre, Les Voiles de Saint-Tropez réunissent yachts classiques et modernes dans le golfe. Le village des régates s'installe sur le vieux port et la presqu'île se remplit : la route unique d'accès sature et le stationnement devient introuvable.",
      "La plupart des invités arrivent par l'aéroport de Nice Côte d'Azur, à une centaine de kilomètres, ou par Toulon-Hyères. Avec le suivi de vol et l'accueil pancarte, nous absorbons les embouteillages de la presqu'île pour vous déposer à l'hôtel sans stress.",
      "Entre régates, déjeuners au port et soirées à Ramatuelle ou Gassin, le rythme est dense. La mise à disposition garde le même chauffeur toute la journée et assure les retours de nuit sur une presqu'île mal desservie après minuit.",
    ],
    bullets: [
      "Accès au vieux port",
      "Transfert aéroport ~100 km",
      "Retours de nuit sur la presqu'île",
    ],
  },
  process: {
    heading: "Réserver un chauffeur pour Les Voiles de Saint-Tropez",
    steps: [
      "Indiquez vos dates de régates, votre hôtel sur la presqu'île et vos horaires.",
      "Nous calons les itinéraires selon les embouteillages d'accès et les fermetures du port.",
      "Le chauffeur se présente en tenue, avec suivi de vol pour vos arrivées à Nice ou Toulon-Hyères.",
      "Transfert direct ou mise à disposition pour couvrir port, déjeuners et soirées.",
    ],
  },
  faq: [
    {
      q: "Comment réserver un chauffeur privé pendant Les Voiles de Saint-Tropez ?",
      a: "Réservez tôt : fin septembre, la presqu'île est saturée et la demande explose. Précisez vos dates, votre hôtel à Saint-Tropez, Ramatuelle ou Gassin et vos horaires pour que nous bloquions un chauffeur dédié.",
    },
    {
      q: "Le chauffeur peut-il m'approcher du vieux port pendant les régates ?",
      a: "Oui. Le village des Voiles occupe le vieux port et les abords sont très encombrés. Le chauffeur vous dépose au point autorisé le plus proche selon la circulation, puis vous reprend à l'endroit convenu.",
    },
    {
      q: "Gérez-vous le transfert depuis l'aéroport de Nice ?",
      a: "Oui, avec suivi de vol et accueil pancarte. Nice Côte d'Azur est à une centaine de kilomètres de Saint-Tropez ; nous anticipons les embouteillages de la presqu'île, et Toulon-Hyères peut servir d'alternative plus proche.",
    },
    {
      q: "Le stationnement est-il vraiment impossible à Saint-Tropez ?",
      a: "Pendant Les Voiles, se garer près du port relève de l'exploit et les parkings sont pleins. Le chauffeur vous évite complètement la recherche : il dépose, patiente ou revient à l'heure convenue.",
    },
    {
      q: "Assurez-vous les retours tardifs après les soirées ?",
      a: "Oui. Les dîners et soirées à Saint-Tropez, Ramatuelle ou Gassin finissent tard, et la presqu'île est mal desservie la nuit : le chauffeur patiente et garantit votre retour direct à l'hôtel.",
    },
    {
      q: "Quels véhicules proposez-vous ?",
      a: "Berlines, SUV et vans avec chauffeur selon la taille de votre groupe, pour un trajet confortable entre l'hôtel, le port et les soirées.",
    },
    {
      q: "Combien de temps prévoir pour rejoindre la presqu'île ?",
      a: "Tout dépend du trafic : fin septembre, l'accès à Saint-Tropez peut être long depuis l'aéroport de Nice. Nous planifions large et suivons la circulation en temps réel pour éviter de manquer un départ de régate ou un dîner.",
    },
  ],
  relatedLinks: [
    {
      label: "Mise à disposition avec chauffeur à Saint-Tropez",
      href: "/chauffeur/saint-tropez",
    },
    {
      label: "Réserver un transfert aéroport",
      href: "/chauffeur/aeroport",
    },
    {
      label: "Mise à disposition avec chauffeur Côte d’Azur",
      href: "/chauffeur/cote-d-azur",
    },
    {
      label: "Réserver un chauffeur à Cannes",
      href: "/chauffeur/cannes",
    },
  ],
  cta: {
    title: "Votre chauffeur pour Les Voiles de Saint-Tropez",
    text: "Transfert aéroport, accès au vieux port et retours de nuit : réservez un chauffeur dédié pour la semaine des régates.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver un chauffeur",
  },
  seo: {
    title: "Chauffeur privé Voiles de Saint-Tropez | KS Rent",
    description:
      "Chauffeur privé pendant Les Voiles de Saint-Tropez : transfert aéroport de Nice, accès au vieux port malgré les embouteillages et retours de nuit.",
    canonical: "/chauffeur/les-voiles-de-saint-tropez",
  },
};

export default lesVoilesDeSaintTropez;
